import { logNetworkRequest } from 'Diagnostics/diagnostics';
import { CustomErrorType } from 'Models/index';
import type { VehicleExtra } from 'Models/vehicle';
import { isObjectRecord } from 'Utils/guards';

import { asCmError, GET, GETBACKUP, POST, POSTBACKUP } from './common/httpClient';
import { CUSTOMER_VEHICLES_ENDPOINT } from './endpoints';

export interface CustomerGarageResponse {
  Garage: VehicleExtra[];
  SelectedVehicle?: VehicleExtra;
}

export async function saveCustomerGarage(
  customerId: string,
  garage: VehicleExtra[],
  selectedVehicle: VehicleExtra,
): Promise<void> {
  try {
    await POST(CUSTOMER_VEHICLES_ENDPOINT, { customerId }, JSON.stringify({ garage, selectedVehicle }), {
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
    });
  } catch (primaryFailure) {
    const primaryError = asCmError(primaryFailure);

    if (primaryError.type === CustomErrorType.requestAborted) {
      return;
    }

    if (primaryError.type !== CustomErrorType.failedToFetch) {
      console.warn(primaryFailure || "Failed to save the customer's garage");
    }

    try {
      await POSTBACKUP(
        CUSTOMER_VEHICLES_ENDPOINT,
        { customerId },
        JSON.stringify({ garage, selectedVehicle }),
        {
          headers: { 'Content-Type': 'application/json' },
          keepalive: true,
        },
      );
      logNetworkRequest({
        endpoint: CUSTOMER_VEHICLES_ENDPOINT,
        scenario: 'backup_used',
        primaryError,
      });
    } catch (backupFailure) {
      const backupError = asCmError(backupFailure);
      console.warn(backupFailure || "Failed to save the customer's garage");
      logNetworkRequest({
        endpoint: CUSTOMER_VEHICLES_ENDPOINT,
        scenario: 'backup_failed',
        primaryError,
        backupError,
      });
    }
  }
}

export async function getCustomerGarage(
  customerId: string,
  signal?: AbortSignal,
): Promise<CustomerGarageResponse | undefined> {
  if (signal?.aborted) {
    return undefined;
  }
  try {
    return getCustomerGarageResponse(
      await GET(
        CUSTOMER_VEHICLES_ENDPOINT,
        { customerId },
        {
          headers: { 'Content-Type': 'application/json' },
          keepalive: true,
          signal,
        },
      ),
    );
  } catch (primaryFailure) {
    const primaryError = asCmError(primaryFailure);

    if (primaryError.type === CustomErrorType.requestAborted) {
      return undefined;
    }

    if (primaryError.type !== CustomErrorType.failedToFetch) {
      console.warn(primaryFailure || "Failed to load the customer's garage");
    }

    if (signal?.aborted) {
      return undefined;
    }

    try {
      const backupResponse = getCustomerGarageResponse(
        await GETBACKUP(
          CUSTOMER_VEHICLES_ENDPOINT,
          { customerId },
          {
            headers: { 'Content-Type': 'application/json' },
            keepalive: true,
            signal,
          },
        ),
      );
      logNetworkRequest({
        endpoint: CUSTOMER_VEHICLES_ENDPOINT,
        scenario: 'backup_used',
        primaryError,
      });
      return backupResponse;
    } catch (backupFailure) {
      const backupError = asCmError(backupFailure);
      console.warn(backupFailure || "Failed to load the customer's garage");
      logNetworkRequest({
        endpoint: CUSTOMER_VEHICLES_ENDPOINT,
        scenario: 'backup_failed',
        primaryError,
        backupError,
      });
    }
  }
}

function getCustomerGarageResponse(value: unknown): CustomerGarageResponse | undefined {
  return isCustomerGarageResponse(value) ? value : undefined;
}

export function isCustomerGarageResponse(value: unknown): value is CustomerGarageResponse {
  if (!isObjectRecord(value) || !Array.isArray(value.Garage) || !value.Garage.every(isVehicleExtra)) {
    return false;
  }

  return value.SelectedVehicle === undefined || isVehicleExtra(value.SelectedVehicle);
}

export function isVehicleExtra(value: unknown): value is VehicleExtra {
  return isObjectRecord(value) && Object.values(value).every((fieldValue) => typeof fieldValue === 'string');
}
